import {
  createEffect,
  createSignal,
  For,
  on,
  Show,
  type JSX,
} from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";
import { ChargesPage } from "./ChargesPage";
import {
  CrudCollectionPage,
  type CrudField,
  type CrudRecord,
} from "./CrudCollectionPage";

type ConfigSection = {
  name: string;
  label: string;
  endpoint: string;
  viewPermission: Parameters<typeof hasPermission>[0];
  createPermission?: Parameters<typeof hasPermission>[0];
  updatePermission?: Parameters<typeof hasPermission>[0];
  deletePermission?: Parameters<typeof hasPermission>[0];
  fields: CrudField[];
  defaults: CrudRecord;
  prepareWrite: (item: CrudRecord) => CrudRecord;
  title: (item: CrudRecord) => string;
  description?: (item: CrudRecord) => string;
  canDelete?: boolean;
  renderAfterFields?: (item: CrudRecord) => JSX.Element;
};

const sections: ConfigSection[] = [
  {
    name: "Departments",
    label: "Departments",
    endpoint: "/departments",
    viewPermission: "config.departments.view",
    createPermission: "config.departments.create",
    updatePermission: "config.departments.update",
    fields: [
      { key: "name", label: "Name" },
      { key: "shortName", label: "Short Name" },
    ],
    defaults: { name: "", shortName: "" },
    prepareWrite: (item) => ({
      name: value(item.name),
      shortName: value(item.shortName),
    }),
    title: (item) => value(item.name),
    description: (item) => value(item.shortName),
  },
  {
    name: "Ranks",
    label: "Ranks",
    endpoint: "/ranks",
    viewPermission: "config.ranks.view",
    createPermission: "config.ranks.create",
    updatePermission: "config.ranks.update",
    fields: [
      { key: "departmentId", label: "Department ID", type: "number" },
      { key: "departmentName", label: "Department", readOnly: true },
      { key: "name", label: "Name" },
      { key: "level", label: "Level", type: "number" },
    ],
    defaults: {
      departmentId: null,
      name: "",
      level: 0,
      permissions: {},
    },
    prepareWrite: (item) => ({
      departmentId: nullableNumber(item.departmentId),
      name: value(item.name),
      level: Math.max(0, Number(item.level) || 0),
      permissions:
        item.permissions && typeof item.permissions === "object"
          ? item.permissions
          : {},
    }),
    title: (item) => value(item.name),
    description: (item) =>
      `${value(item.departmentName)} • Level ${value(item.level)}`,
    renderAfterFields: (item) => <RankPermissions item={item} />,
  },
  {
    name: "Tags",
    label: "Tags",
    endpoint: "/tags",
    viewPermission: "tags.view",
    createPermission: "tags.create",
    updatePermission: "tags.update",
    deletePermission: "tags.delete",
    fields: [
      { key: "name", label: "Name" },
      { key: "color", label: "Color" },
    ],
    defaults: { name: "", color: "#00f8b9" },
    prepareWrite: (item) => ({
      name: value(item.name),
      color: value(item.color) || "#00f8b9",
    }),
    title: (item) => value(item.name),
    description: (item) => value(item.color),
    canDelete: true,
  },
  {
    name: "Categories",
    label: "Report Categories",
    endpoint: "/report-categories",
    viewPermission: "config.categories.update",
    createPermission: "config.categories.create",
    updatePermission: "config.categories.update",
    fields: [
      { key: "name", label: "Name" },
      { key: "color", label: "Color" },
      { key: "icon", label: "Icon" },
      { key: "hasReceipts", label: "Read receipts", type: "checkbox" },
      { key: "hasPetition", label: "Petitions", type: "checkbox" },
      { key: "hasSignatures", label: "Signature types (JSON)" },
      { key: "defaultTemplate", label: "Default template (Quill JSON)" },
    ],
    defaults: { name: "", color: "#00f8b9", icon: "file-text", hasReceipts: false, hasPetition: false, hasSignatures: {}, defaultTemplate: { ops: [] } },
    prepareWrite: (item) => ({
      name: value(item.name),
      color: value(item.color) || "#00f8b9",
      icon: value(item.icon) || "file-text",
      hasReceipts: item.hasReceipts === true,
      hasPetition: item.hasPetition === true,
      hasSignatures: parseJsonObject(item.hasSignatures),
      defaultTemplate: parseJsonValue(item.defaultTemplate, { ops: [] }),
    }),
    title: (item) => value(item.name),
    description: (item) => `${value(item.icon)} • ${value(item.color)}`,
  },
];

export function ConfigPage() {
  const visibleSections = () => sections.filter((item) => hasPermission(item.viewPermission));
  const tabs = () => [
    ...visibleSections().map((section) => section.name),
    ...(hasPermission("config.charges.view") ? ["Charges"] : []),
  ];
  const [active, setActive] = createSignal<string>(tabs()[0] ?? "");
  const selected = () => visibleSections().find((item) => item.name === active());

  return (
    <div class="flex h-full w-full flex-col">
      <ul class="mb-4 flex h-12 w-full items-end justify-start gap-x-1">
        <For each={tabs()}>
          {(name) => (
            <li
              class={`flex cursor-pointer items-center justify-center px-4 py-2 ${
                active() === name
                  ? "h-12 border-b-4 border-cyan bg-white-11"
                  : "h-full bg-gray-gradient hover:bg-white-11"
              }`}
              onClick={() => setActive(name)}
            >
              <p class="roboto500">{name}</p>
            </li>
          )}
        </For>
      </ul>

      <div class="min-h-0 flex-1">
        <Show when={active() === "Charges"} fallback={
          <Show when={selected()}>
            {(section) => (
              <CrudCollectionPage
                label={section().label}
                endpoint={section().endpoint}
                icon="nav_config"
                fields={section().fields}
                createDefaults={section().defaults}
                canCreate={section().createPermission ? hasPermission(section().createPermission) : false}
                canUpdate={section().updatePermission ? hasPermission(section().updatePermission) : false}
                canDelete={section().deletePermission ? hasPermission(section().deletePermission) : false}
                loadDetail={false}
                advancedSearch={false}
                itemTitle={section().title}
                itemDescription={section().description}
                prepareWrite={(item) => section().prepareWrite(item)}
                renderAfterFields={(item) => section().renderAfterFields?.(item)}
              />
            )}
          </Show>
        }>
          <ChargesPage />
        </Show>
      </div>
    </div>
  );
}

type PermissionRecord = CrudRecord & {
  id: number;
  name: string;
  information: boolean | string[];
};

function RankPermissions(props: { item: CrudRecord }) {
  const [permissions, setPermissions] = createSignal<PermissionRecord[]>([]);
  const [saving, setSaving] = createSignal(false);
  const [saved, setSaved] = createSignal(false);
  const id = () => Number(props.item.id);

  const load = async () => {
    if (!id()) {
      setPermissions([]);
      return;
    }
    setPermissions(
      await apiRequest<PermissionRecord[]>(`/ranks/${id()}/permissions`, {
        token: currentUser()?.accessToken,
      }).catch(() => []),
    );
  };
  createEffect(on(id, () => void load(), { defer: false }));

  const setEnabled = (permissionId: number, enabled: boolean) => {
    setSaved(false);
    setPermissions((current) =>
      current.map((permission) =>
        permission.id === permissionId
          ? { ...permission, information: enabled }
          : permission,
      ),
    );
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiRequest(`/ranks/${id()}/permissions`, {
        method: "PATCH",
        token: currentUser()?.accessToken,
        body: {
          permissions: permissions().map(({ id, information }) => ({
            id,
            information,
          })),
        },
      });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Show when={id()}>
      <section class="bg-white-5 p-3">
        <div class="mb-3 flex items-center justify-between">
          <div>
            <p class="roboto500">Rank Permissions</p>
            <p class="text-xs text-white-35">
              Permissions exposed by the reversed MDT contract.
            </p>
          </div>
          <button
            class="bg-green-gradient px-4 py-2 disabled:opacity-40"
            disabled={saving()}
            onClick={() => void save()}
          >
            {saved() ? "Saved" : "Save Permissions"}
          </button>
        </div>
        <div class="grid max-h-80 grid-cols-2 gap-2 overflow-y-auto pr-2 xl:grid-cols-3">
          <For each={permissions()}>
            {(permission) => (
              <label class="flex cursor-pointer items-center gap-2 bg-gray-gradient px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={
                    permission.information === true ||
                    (Array.isArray(permission.information) &&
                      permission.information.length > 0)
                  }
                  onChange={(event) =>
                    setEnabled(permission.id, event.currentTarget.checked)
                  }
                />
                <span>{permission.name}</span>
              </label>
            )}
          </For>
        </div>
      </section>
    </Show>
  );
}

function value(input: unknown): string {
  return typeof input === "string" || typeof input === "number"
    ? String(input)
    : "";
}

function parseJsonValue(input: unknown, fallback: unknown): unknown {
  if (typeof input === "object" && input !== null) return input;
  if (typeof input !== "string" || !input.trim()) return fallback;
  try {
    return JSON.parse(input);
  } catch {
    return fallback;
  }
}

function parseJsonObject(input: unknown): Record<string, unknown> {
  const parsed = parseJsonValue(input, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed as Record<string, unknown>
    : {};
}

function nullableNumber(input: unknown): number | null {
  const output = Number(input);
  return Number.isSafeInteger(output) && output > 0 ? output : null;
}
